import React, { lazy } from 'react';
import { Droplets, Maximize, Layout, Square, SlidersHorizontal, Bath, DoorOpen, Wrench, Check } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
export function ShowerStyles() {
  const styles = [{
    title: 'Frameless Shower Doors',
    copy: 'A clean, modern glass shower door with minimal hardware that keeps the bathroom open, bright and focused on your tile or stone work.',
    icon: Maximize
  }, {
    title: 'Euro Glass Shower Doors',
    copy: 'European-style frameless shower doors made with thick tempered glass and sleek hardware for a luxury, spa-like bathroom feel.',
    icon: Layout
  }, {
    title: 'Semi-Frameless Shower Doors',
    copy: 'A balanced option that combines clean glass lines with added framing support for a polished, practical look.',
    icon: Square
  }, {
    title: 'Framed Glass Shower Doors',
    copy: 'A classic and cost-effective shower door style with frame support, available in multiple finishes and glass options.',
    icon: DoorOpen
  }, {
    title: 'Sliding & Bypass Shower Doors',
    copy: 'Space-saving glass shower doors that work well in tighter bathrooms, tub openings and layouts where a swing door is not ideal.',
    icon: SlidersHorizontal
  }, {
    title: 'Tub Glass Enclosures',
    copy: 'Upgrade a bathtub-shower combination with custom glass panels or doors instead of a shower curtain.',
    icon: Bath
  }, {
    title: 'Walk-In & Barrier-Free Shower Glass',
    copy: 'Fixed panels and custom glass layouts for low-curb, curbless or barrier-free shower designs.',
    icon: Droplets
  }, {
    title: 'Shower Door Replacement & Repair',
    copy: 'Replacement and repair options for damaged, outdated, leaking or poorly fitting glass shower doors and enclosures.',
    icon: Wrench
  }];
  return <section className="py-20 bg-gray-50" data-id="element-159">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-160">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-161">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-162">
            Custom Shower Glass Designed Around Your Bathroom
          </h2>
          <p className="text-lg text-gray-600" data-id="element-163">
            Every bathroom is different. Kosin’s Glass helps you choose the
            shower glass, door style, hardware and finish that fit your layout,
            taste and daily routine.
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-164">
          {styles.map((style, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100 hover:shadow-soft transition-shadow" data-id="element-165">
              <div className="w-12 h-12 bg-glass rounded-xl flex items-center justify-center mb-6 text-primary" data-id="element-166">
                <style.icon className="w-6 h-6" data-id="element-167" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-168">
                {style.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-169">{style.copy}</p>
            </FadeIn>)}
        </div>

        <FadeIn className="text-center" data-id="element-170">
          <a href="#quote-form" className="inline-flex items-center justify-center bg-primary hover:bg-primary-hover text-white font-semibold py-4 px-8 rounded-xl transition-colors" data-id="element-171">
            Request a Free Shower Glass Quote
          </a>
        </FadeIn>
      </div>
    </section>;
}
export function Comparison() {
  const comparisons = [{
    title: 'Frameless / Euro',
    bestFor: ['Modern bathroom remodels', 'Showcasing tile or stone', 'Open, airy bathroom designs', 'Homeowners wanting minimal hardware', 'Luxury master bathrooms']
  }, {
    title: 'Semi-Frameless / Framed',
    bestFor: ['Traditional or transitional bathrooms', 'Practical support with a clean look', 'Budget-conscious upgrades', 'Replacing older framed doors', 'Tub and shower combinations']
  }, {
    title: 'Sliding / Bypass',
    bestFor: ['Smaller bathrooms', 'Tub enclosures', 'Openings without swing-door clearance', 'Guest bathrooms', 'Efficient daily use']
  }, {
    title: 'Fixed Panel / Walk-In',
    bestFor: ['Low-curb or curbless showers', 'Accessible bathroom layouts', 'Minimalist designs', 'Open-concept shower spaces', 'Barrier-free entry goals']
  }];
  return <section className="py-20 bg-white" data-id="element-172">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-173">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-174">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-175">
            Which Glass Shower Door Is Right for Your Bathroom?
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-176">
          {comparisons.map((item, i) => <FadeIn key={i} delay={i * 0.1} className="bg-gray-50 p-8 rounded-2xl border border-gray-100" data-id="element-177">
              <h3 className="text-xl font-bold text-navy mb-6 pb-4 border-b border-gray-200" data-id="element-178">
                {item.title}
              </h3>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-179">
                Best for:
              </p>
              <ul className="space-y-3" data-id="element-180">
                {item.bestFor.map((bullet, j) => <li key={j} className="flex items-start gap-2" data-id="element-181">
                    <Check className="w-5 h-5 text-primary shrink-0" data-id="element-182" />
                    <span className="text-gray-700" data-id="element-183">{bullet}</span>
                  </li>)}
              </ul>
            </FadeIn>)}
        </div>

        <FadeIn className="max-w-2xl mx-auto text-center bg-glass p-6 rounded-2xl mb-8" data-id="element-184">
          <p className="text-navy font-medium" data-id="element-185">
            Kosin’s Glass can review your layout, measurements, bathroom style
            and usage needs to recommend the best shower glass option.
          </p>
        </FadeIn>

        <FadeIn className="text-center" data-id="element-186">
          <a href="#quote-form" className="inline-flex items-center justify-center bg-navy hover:bg-navy-800 text-white font-semibold py-4 px-8 rounded-xl transition-colors" data-id="element-187">
            Help Me Choose My Shower Door
          </a>
        </FadeIn>
      </div>
    </section>;
}
export function DesignOptions() {
  const options = [{
    title: 'Glass Designs & Textures',
    copy: 'Choose from clear glass, frosted glass, rain glass, reeded glass, textured glass, tinted glass and other decorative options. Kosin’s offers 18 designs and textures customized to fit your space.'
  }, {
    title: 'Hardware Finishes',
    copy: 'Select hinges, handles, clips, towel bars and brackets in finishes that complement your bathroom, including modern black, brushed nickel, brass-inspired tones and other available options.'
  }, {
    title: 'Protective Coatings',
    copy: 'Ask about water-repellent glass coatings that help reduce water spots, soap scum and everyday cleaning time.'
  }];
  const gallery = [{
    url: 'https://cdn.magicpatterns.com/uploads/4fXA2y9HE9kV4VPD5tfUj4/GSDpic5.webp',
    alt: 'Custom frameless glass shower enclosure with marble tile installed by Kosin’s Glass',
    caption: 'Frameless Enclosure'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/o43RcMjWkzoqErKvc88Bft/GSDpic1.webp',
    alt: 'Frameless glass shower with neo-angle layout, subway tile and bench seat by Kosin’s Glass',
    caption: 'Neo-Angle Frameless'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/3dFEqBspuy24G379oFCrkW/GSDpic3.webp',
    alt: 'Frameless walk-in glass shower with frosted privacy panel, marble walls and teak bench by Kosin’s Glass',
    caption: 'Walk-In with Frosted Panel'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/7geDRzwyJG9LzAFVFRRYmw/GSDpic2.webp',
    alt: 'Frameless glass corner shower enclosure with textured tile and candle ledge by Kosin’s Glass',
    caption: 'Frameless Corner Enclosure'
  }];
  return <section className="py-20 bg-navy text-white overflow-hidden" data-id="element-188">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-189">
        <FadeIn className="max-w-3xl mb-16" data-id="element-190">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-191">
            Glass, Hardware & Finish Options That Fit Your Style
          </h2>
          <p className="text-lg text-blue-100" data-id="element-192">
            Your shower glass should match the rest of your bathroom. Kosin’s
            Glass offers a wide selection of glass textures, designs, hardware
            finishes, colors and styles so your enclosure feels custom from
            every angle.
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-193">
          {options.map((opt, i) => <FadeIn key={i} delay={i * 0.1} className="bg-navy-800 p-8 rounded-2xl border border-navy-700" data-id="element-194">
              <h3 className="text-xl font-bold mb-4" data-id="element-195">{opt.title}</h3>
              <p className="text-blue-100 leading-relaxed" data-id="element-196">{opt.copy}</p>
            </FadeIn>)}
        </div>

        <FadeIn delay={0.3} data-id="element-197">
          <div className="mb-8 flex items-end justify-between flex-wrap gap-3" data-id="element-198">
            <div data-id="element-199">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-2" data-id="element-200">
                Recent Projects
              </p>
              <h3 className="text-2xl md:text-3xl font-serif font-semibold tracking-wide" data-id="element-201">
                Custom Shower Glass Installed by Kosin’s
              </h3>
            </div>
            <p className="text-sm text-blue-100/80 max-w-sm" data-id="element-202">
              A look at real frameless and custom shower enclosures designed,
              measured and installed for Southeast Michigan homeowners.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-203">
            {gallery.map((img, i) => <figure key={i} className="aspect-[3/4] rounded-2xl overflow-hidden bg-navy-800 relative group ring-1 ring-white/10" data-id="element-204">
                <img src={img.url} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" data-id="element-205" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" data-id="element-206" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-4" data-id="element-207">
                  <span className="text-xs uppercase tracking-[0.18em] text-white/70 block mb-1" data-id="element-208">
                    Kosin’s Install
                  </span>
                  <span className="text-sm md:text-base font-display italic text-white" data-id="element-209">
                    {img.caption}
                  </span>
                </figcaption>
              </figure>)}
          </div>
        </FadeIn>
      </div>
    </section>;
}